import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLightbulb, faPlus, faTimes, faCheck, faTrash,
  faRobot, faClock, faCalendar, faExclamationTriangle,
  faSpinner, faMagic, faTag, faStickyNote,
  faCheckCircle, faFilter, faSearch,
  faChartPie, faFileAlt, faArrowRight, faThumbtack,
  faChartBar, faExternalLinkAlt, faLock,
} from '@fortawesome/free-solid-svg-icons';
import { remindersAPI, notesAPI, aiAPI, workspacesAPI } from '../services/api';
import { useRBAC } from '../hooks/useRBAC';
import useI18n from '../hooks/useI18n';
import '../styles/AIReminders.css';

// ==================== Types ====================
interface AIReminder {
  id: string;
  title: string;
  description: string | null;
  type: string;
  priority: string;
  dueDate: string | null;
  itemId?: string | null;
  itemName?: string | null;
  workspaceId?: string | null;
  assigneeId?: string | null;
  assignee?: { id: string; firstName?: string | null; lastName?: string | null; email?: string | null } | null;
  isCompleted: boolean;
  isAISuggestion: boolean;
  aiConfidence?: number | null;
  createdAt: string;
  updatedAt?: string;
}

interface SmartNote {
  id: string;
  content: string;
  itemId?: string | null;
  itemName?: string | null;
  workspaceId?: string | null;
  assigneeId?: string | null;
  assignee?: { id: string; firstName?: string | null; lastName?: string | null; email?: string | null } | null;
  tags: string[];
  isAIGenerated: boolean;
  aiContext?: string | null;
  isPinned: boolean;
  createdAt: string;
  updatedAt?: string;
}

interface WorkspaceMember {
  id: string;
  userId: string;
  role: string;
  user: {
    id: string;
    email: string;
    firstName?: string | null;
    lastName?: string | null;
  };
}

interface AIInsight {
  id: string;
  type: 'suggestion' | 'warning' | 'tip' | 'analysis';
  title: string;
  description: string;
  actionLabel?: string;
  actionType?: string;
  relatedItems?: { id: string; name: string; isLocked?: boolean }[];
  confidence: number;
  createdAt: string;
  isDismissed: boolean;
}

interface AISummary {
  totalItems: number;
  totalValue: number;
  averageValue?: number;
  categoriesUsed: number;
  itemsWithPhotos: number;
  itemsWithPrices: number;
  topCategories: { name: string; count: number }[];
  conditionBreakdown: Record<string, number>;
  recentItemsCount?: number;
  mostValuableItem?: { id: string; name: string; value: number; isLocked?: boolean } | null;
  summary: string;
}

interface AIReport {
  generatedAt: string;
  itemCount: number;
  totalValue: number;
  sections: {
    title: string;
    type: string;
    content: string;
    stats?: any;
    data?: any;
  }[];
}

// ==================== Helper Functions ====================
const getPriorityColor = (priority: string) => {
  switch (priority) {
    case 'urgent': return '#ef4444';
    case 'high': return '#f97316';
    case 'medium': return '#eab308';
    case 'low': return '#10b981';
    default: return '#6b7280';
  }
};

const getTypeIcon = (type: string) => {
  switch (type) {
    case 'maintenance': return faClock;
    case 'review': return faSearch;
    case 'insurance': return faExclamationTriangle;
    case 'warranty': return faCheckCircle;
    case 'ai-suggested': return faRobot;
    default: return faCalendar;
  }
};

const formatUserLabel = (user?: { firstName?: string | null; lastName?: string | null; email?: string | null } | null, t?: (key: string) => string) => {
  if (!user) return t ? t('smartHub.unassigned') : 'Unassigned';
  const name = `${user.firstName || ''} ${user.lastName || ''}`.trim();
  return name || user.email || (t ? t('smartHub.member') : 'Member');
};

const formatRelativeTime = (dateString: string, t?: (key: string, params?: Record<string, string | number>) => string) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays < 0) return t ? t('smartHub.daysOverdue', { count: Math.abs(diffDays) }) : `${Math.abs(diffDays)} days overdue`;
  if (diffDays === 0) return t ? t('smartHub.today') : 'Today';
  if (diffDays === 1) return t ? t('smartHub.tomorrow') : 'Tomorrow';
  if (diffDays <= 7) return t ? t('smartHub.inDays', { count: diffDays }) : `In ${diffDays} days`;
  if (diffDays <= 30) return t ? t('smartHub.inWeeks', { count: Math.ceil(diffDays / 7) }) : `In ${Math.ceil(diffDays / 7)} weeks`;
  return t ? t('smartHub.inMonths', { count: Math.ceil(diffDays / 30) }) : `In ${Math.ceil(diffDays / 30)} months`;
};

/** Mask a locked item name — show first char + asterisks */
const maskItemName = (name: string): string => {
  if (!name || name.length <= 1) return '****';
  return name.charAt(0) + '*'.repeat(Math.min(name.length - 1, 7));
};

// ==================== Main Component ====================
const AIReminders: React.FC = () => {
  const { currentWorkspace } = useRBAC();
  const navigate = useNavigate();
  const { t } = useI18n();

  // State
  const [reminders, setReminders] = useState<AIReminder[]>([]);
  const [notes, setNotes] = useState<SmartNote[]>([]);
  const [insights, setInsights] = useState<AIInsight[]>([]);
  const [summary, setSummary] = useState<AISummary | null>(null);
  const [report, setReport] = useState<AIReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [generatingAI, setGeneratingAI] = useState(false);
  const [loadingReport, setLoadingReport] = useState(false);
  const [activeTab, setActiveTab] = useState<'overview' | 'reminders' | 'notes' | 'insights' | 'report'>('overview');
  const [workspaceMembers, setWorkspaceMembers] = useState<WorkspaceMember[]>([]);
  const [currentUser, setCurrentUser] = useState<{ id: string; name: string; email?: string | null } | null>(null);

  // Form states
  const [showReminderForm, setShowReminderForm] = useState(false);
  const [showNoteForm, setShowNoteForm] = useState(false);
  const [editingReminder, setEditingReminder] = useState<string | null>(null);
  const [editingNote, setEditingNote] = useState<string | null>(null);

  // Filters
  const [reminderFilter, setReminderFilter] = useState<'all' | 'active' | 'completed'>('active');
  const [noteFilter, setNoteFilter] = useState<'all' | 'pinned' | 'ai-generated'>('all');

  // New reminder form
  const [newReminder, setNewReminder] = useState({
    title: '', description: '', type: 'custom', priority: 'medium', dueDate: '', dueTime: '', assigneeId: '',
  });

  // New note form
  const [newNote, setNewNote] = useState({
    content: '', tags: [] as string[], tagInput: '', assigneeId: '',
  });

  // Dismissed insights (local only)
  const [dismissedInsights, setDismissedInsights] = useState<Set<string>>(new Set());

  // ==================== Data Fetching ====================
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const workspaceId = currentWorkspace?.id;
      const [remindersRes, notesRes, insightsRes, summaryRes] = await Promise.allSettled([
        remindersAPI.getAll(workspaceId ? { workspaceId } : undefined),
        notesAPI.getAll(workspaceId ? { workspaceId } : undefined),
        aiAPI.getInsights(),
        aiAPI.getSummary(),
      ]);

      if (remindersRes.status === 'fulfilled') setReminders(remindersRes.value.data || []);
      if (notesRes.status === 'fulfilled') setNotes(notesRes.value.data || []);
      if (insightsRes.status === 'fulfilled') setInsights(insightsRes.value.data || []);
      if (summaryRes.status === 'fulfilled') setSummary(summaryRes.value.data || null);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, [currentWorkspace?.id]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) return;
    try {
      const parsed = JSON.parse(storedUser);
      const fullName = `${parsed.firstName || ''} ${parsed.lastName || ''}`.trim();
      setCurrentUser({ id: parsed.id, name: fullName || parsed.email || 'Me', email: parsed.email });
    } catch (error) {
      console.error('Failed to parse user from localStorage:', error);
    }
  }, []);

  useEffect(() => {
    if (currentUser?.id) {
      setNewReminder(prev => ({ ...prev, assigneeId: prev.assigneeId || currentUser.id }));
      setNewNote(prev => ({ ...prev, assigneeId: prev.assigneeId || currentUser.id }));
    }
  }, [currentUser?.id]);

  useEffect(() => {
    const loadMembers = async () => {
      if (!currentWorkspace?.id) {
        setWorkspaceMembers([]);
        return;
      }
      try {
        const res = await workspacesAPI.getMembers(currentWorkspace.id);
        setWorkspaceMembers(res.data?.members || []);
      } catch (error) {
        console.error('Failed to load workspace members:', error);
      }
    };

    loadMembers();
  }, [currentWorkspace?.id]);

  const fetchReport = async () => {
    setLoadingReport(true);
    try {
      const res = await aiAPI.getReport();
      setReport(res.data);
    } catch (error) {
      console.error('Failed to fetch report:', error);
    } finally {
      setLoadingReport(false);
    }
  };

  // ==================== AI Generation ====================
  const generateAISuggestions = async () => {
    setGeneratingAI(true);
    try {
      const [insightsRes, remindersRes] = await Promise.allSettled([
        aiAPI.getInsights(),
        aiAPI.generateReminders(),
      ]);

      if (insightsRes.status === 'fulfilled') setInsights(insightsRes.value.data || []);
      if (remindersRes.status === 'fulfilled' && remindersRes.value.data.reminders) {
        setReminders(prev => [...remindersRes.value.data.reminders, ...prev]);
      }

      // Refresh summary
      const summaryRes = await aiAPI.getSummary();
      setSummary(summaryRes.data);
    } catch (error) {
      console.error('Failed to generate AI suggestions:', error);
    } finally {
      setGeneratingAI(false);
    }
  };

  // ==================== CRUD Operations ====================
  const handleCreateReminder = async () => {
    if (editingReminder) {
      await handleUpdateReminder();
      return;
    }
    
    if (!newReminder.title.trim()) return;
    try {
      const dueDate = newReminder.dueDate
        ? new Date(`${newReminder.dueDate}T${newReminder.dueTime || '09:00'}`).toISOString()
        : undefined;

      const res = await remindersAPI.create({
        title: newReminder.title,
        description: newReminder.description || undefined,
        type: newReminder.type,
        priority: newReminder.priority,
        dueDate,
        assigneeId: newReminder.assigneeId || undefined,
        workspaceId: currentWorkspace?.id || undefined,
      });
      setReminders(prev => [res.data, ...prev]);
      setNewReminder({
        title: '',
        description: '',
        type: 'custom',
        priority: 'medium',
        dueDate: '',
        dueTime: '',
        assigneeId: currentUser?.id || '',
      });
      setShowReminderForm(false);
    } catch (error) {
      console.error('Failed to create reminder:', error);
    }
  };

  const handleToggleReminder = async (id: string) => {
    try {
      const res = await remindersAPI.toggle(id);
      setReminders(prev => prev.map(r => r.id === id ? res.data : r));
    } catch (error) {
      console.error('Failed to toggle reminder:', error);
      // Optimistic fallback
      setReminders(prev => prev.map(r => r.id === id ? { ...r, isCompleted: !r.isCompleted } : r));
    }
  };

  const handleDeleteReminder = async (id: string) => {
    try {
      await remindersAPI.delete(id);
      setReminders(prev => prev.filter(r => r.id !== id));
    } catch (error) {
      console.error('Failed to delete reminder:', error);
    }
  };

  const handleEditReminder = (reminder: AIReminder) => {
    setEditingReminder(reminder.id);
    const dueDate = reminder.dueDate ? new Date(reminder.dueDate) : null;
    setNewReminder({
      title: reminder.title,
      description: reminder.description || '',
      type: reminder.type,
      priority: reminder.priority,
      dueDate: dueDate ? dueDate.toISOString().split('T')[0] : '',
      dueTime: dueDate ? dueDate.toTimeString().slice(0, 5) : '',
      assigneeId: reminder.assigneeId || '',
    });
    setShowReminderForm(true);
  };

  const handleUpdateReminder = async () => {
    if (!editingReminder || !newReminder.title.trim()) return;
    try {
      const dueDate = newReminder.dueDate
        ? new Date(`${newReminder.dueDate}T${newReminder.dueTime || '09:00'}`).toISOString()
        : undefined;

      const res = await remindersAPI.update(editingReminder, {
        title: newReminder.title,
        description: newReminder.description || undefined,
        type: newReminder.type,
        priority: newReminder.priority,
        dueDate,
        assigneeId: newReminder.assigneeId || undefined,
      });
      setReminders(prev => prev.map(r => r.id === editingReminder ? res.data : r));
      setNewReminder({
        title: '',
        description: '',
        type: 'custom',
        priority: 'medium',
        dueDate: '',
        dueTime: '',
        assigneeId: currentUser?.id || '',
      });
      setShowReminderForm(false);
      setEditingReminder(null);
    } catch (error) {
      console.error('Failed to update reminder:', error);
    }
  };

  const handleCreateNote = async () => {
    if (editingNote) {
      await handleUpdateNote();
      return;
    }
    
    if (!newNote.content.trim()) return;
    try {
      const res = await notesAPI.create({
        content: newNote.content,
        tags: newNote.tags,
        assigneeId: newNote.assigneeId || undefined,
        workspaceId: currentWorkspace?.id || undefined,
      });
      setNotes(prev => [res.data, ...prev]);
      setNewNote({ content: '', tags: [], tagInput: '', assigneeId: currentUser?.id || '' });
      setShowNoteForm(false);
    } catch (error) {
      console.error('Failed to create note:', error);
    }
  };

  const handleTogglePin = async (id: string) => {
    try {
      const res = await notesAPI.togglePin(id);
      setNotes(prev => prev.map(n => n.id === id ? res.data : n));
    } catch (error) {
      console.error('Failed to toggle pin:', error);
      setNotes(prev => prev.map(n => n.id === id ? { ...n, isPinned: !n.isPinned } : n));
    }
  };

  const handleDeleteNote = async (id: string) => {
    try {
      await notesAPI.delete(id);
      setNotes(prev => prev.filter(n => n.id !== id));
    } catch (error) {
      console.error('Failed to delete note:', error);
    }
  };

  const handleEditNote = (note: SmartNote) => {
    setEditingNote(note.id);
    setNewNote({
      content: note.content,
      tags: note.tags || [],
      tagInput: '',
      assigneeId: note.assigneeId || '',
    });
    setShowNoteForm(true);
  };

  const handleUpdateNote = async () => {
    if (!editingNote || !newNote.content.trim()) return;
    try {
      const res = await notesAPI.update(editingNote, {
        content: newNote.content,
        tags: newNote.tags,
        assigneeId: newNote.assigneeId || undefined,
      });
      setNotes(prev => prev.map(n => n.id === editingNote ? res.data : n));
      setNewNote({ content: '', tags: [], tagInput: '', assigneeId: currentUser?.id || '' });
      setShowNoteForm(false);
      setEditingNote(null);
    } catch (error) {
      console.error('Failed to update note:', error);
    }
  };

  const handleDismissInsight = (id: string) => {
    setDismissedInsights(prev => new Set(prev).add(id));
  };

  const handleAddTag = () => {
    if (newNote.tagInput.trim() && !newNote.tags.includes(newNote.tagInput.trim())) {
      setNewNote(prev => ({ ...prev, tags: [...prev.tags, prev.tagInput.trim()], tagInput: '' }));
    }
  };

  const handleRemoveTag = (tag: string) => {
    setNewNote(prev => ({ ...prev, tags: prev.tags.filter(t => t !== tag) }));
  };

  const navigateToItem = (itemId: string, _itemName?: string, _isLocked?: boolean) => {
    navigate(`/library?item=${itemId}`);
  };

  // ==================== Filtered Data ====================
  const filteredReminders = reminders.filter(r => {
    if (reminderFilter === 'active') return !r.isCompleted;
    if (reminderFilter === 'completed') return r.isCompleted;
    return true;
  });

  const filteredNotes = notes.filter(n => {
    if (noteFilter === 'pinned') return n.isPinned;
    if (noteFilter === 'ai-generated') return n.isAIGenerated;
    return true;
  });

  const activeInsights = insights.filter(i => !i.isDismissed && !dismissedInsights.has(i.id));
  const activeReminders = reminders.filter(r => !r.isCompleted);
  const overdueReminders = activeReminders.filter(r => r.dueDate && new Date(r.dueDate) < new Date());

  // ==================== Render ====================
  if (loading) {
    return (
      <div className="ai-hub">
        <div className="ai-loading">
          <FontAwesomeIcon icon={faSpinner} spin />
          <span>{t('smartHub.loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="ai-hub">
      {/* Header */}
      <div className="ai-hub-header">
        <div className="header-title">
          <FontAwesomeIcon icon={faRobot} />
          <h2>{t('smartHub.title')}</h2>
          <span className="header-subtitle">{t('smartHub.subtitle')}</span>
        </div>
        <button
          className="ai-generate-btn"
          onClick={generateAISuggestions}
          disabled={generatingAI}
        >
          <FontAwesomeIcon icon={faMagic} spin={generatingAI} />
          {generatingAI ? t('smartHub.analyzing') : t('smartHub.aiAnalyze')}
        </button>
      </div>

      {/* Tab Navigation */}
      <div className="ai-hub-tabs">
        {[
          { key: 'overview', label: t('smartHub.overview'), icon: faChartPie, count: null },
          { key: 'reminders', label: t('smartHub.reminders'), icon: faCalendar, count: activeReminders.length },
          { key: 'notes', label: t('smartHub.notes'), icon: faStickyNote, count: notes.length },
          { key: 'insights', label: t('smartHub.insights'), icon: faLightbulb, count: activeInsights.length },
          { key: 'report', label: t('smartHub.aiReport'), icon: faFileAlt, count: null },
        ].map(tab => (
          <button
            key={tab.key}
            className={`hub-tab ${activeTab === tab.key ? 'active' : ''}`}
            onClick={() => {
              setActiveTab(tab.key as any);
              if (tab.key === 'report' && !report) fetchReport();
            }}
          >
            <FontAwesomeIcon icon={tab.icon} />
            <span>{tab.label}</span>
            {tab.count !== null && tab.count > 0 && (
              <span className="tab-badge">{tab.count}</span>
            )}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="ai-hub-content">
        {/* ==================== OVERVIEW TAB ==================== */}
        {activeTab === 'overview' && (
          <div className="overview-tab">
            {/* Summary Cards */}
            {summary && (
              <div className="summary-grid">
                <div className="summary-card">
                  <div className="card-icon items"><FontAwesomeIcon icon={faChartBar} /></div>
                  <div className="card-info">
                    <span className="card-value">{summary.totalItems}</span>
                    <span className="card-label">{t('smartHub.totalItems')}</span>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="card-icon value"><FontAwesomeIcon icon={faChartPie} /></div>
                  <div className="card-info">
                    <span className="card-value">${summary.totalValue.toLocaleString()}</span>
                    <span className="card-label">{t('smartHub.collectionValue')}</span>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="card-icon categories"><FontAwesomeIcon icon={faTag} /></div>
                  <div className="card-info">
                    <span className="card-value">{summary.categoriesUsed}</span>
                    <span className="card-label">{t('smartHub.categories')}</span>
                  </div>
                </div>
                <div className="summary-card">
                  <div className="card-icon reminders"><FontAwesomeIcon icon={faCalendar} /></div>
                  <div className="card-info">
                    <span className="card-value">{activeReminders.length}</span>
                    <span className="card-label">{t('smartHub.activeReminders')}</span>
                  </div>
                </div>
              </div>
            )}

            {/* AI Summary Text */}
            {summary && (
              <div className="ai-summary-box">
                <div className="summary-header">
                  <FontAwesomeIcon icon={faRobot} />
                  <h4>{t('smartHub.aiSummary')}</h4>
                </div>
                <p className="summary-text">{
                  summary.totalItems === 0 
                    ? t('smartHub.summaryEmpty')
                    : [
                        t('smartHub.summaryContains').replace('{count}', String(summary.totalItems)),
                        summary.totalValue > 0 ? t('smartHub.summaryTotalValue').replace('{total}', `$${summary.totalValue.toLocaleString()}`).replace('{avg}', `$${Math.round(summary.averageValue || (summary.totalValue / summary.totalItems)).toLocaleString()}`) : '',
                        summary.topCategories.length > 0 ? t('smartHub.summaryTopCategory').replace('{name}', summary.topCategories[0].name).replace('{count}', String(summary.topCategories[0].count)) : '',
                        summary.recentItemsCount ? t('smartHub.summaryRecentItems').replace('{count}', String(summary.recentItemsCount)) : '',
                        summary.mostValuableItem && !summary.mostValuableItem.isLocked ? t('smartHub.summaryMostValuable').replace('{name}', summary.mostValuableItem.name).replace('{value}', `$${summary.mostValuableItem.value.toLocaleString()}`) : '',
                      ].filter(Boolean).join(' ')
                }</p>
                {summary.topCategories.length > 0 && (
                  <div className="top-categories">
                    <span className="cat-label">{t('smartHub.topCategories')}</span>
                    {summary.topCategories.map(cat => (
                      <span key={cat.name} className="cat-pill">{cat.name} ({cat.count})</span>
                    ))}
                  </div>
                )}
                {summary.mostValuableItem && (
                  <div className="valuable-item">
                    <span>{t('smartHub.mostValuable')} </span>
                    {summary.mostValuableItem.isLocked ? (
                      <button
                        className="item-link locked-item-link"
                        onClick={() => navigateToItem(summary.mostValuableItem!.id, summary.mostValuableItem!.name, true)}
                      >
                        <FontAwesomeIcon icon={faLock} style={{ marginRight: 4, fontSize: '0.8em' }} />
                        {maskItemName(summary.mostValuableItem.name)}
                      </button>
                    ) : (
                      <button
                        className="item-link"
                        onClick={() => navigateToItem(summary.mostValuableItem!.id)}
                      >
                        {summary.mostValuableItem.name}
                        <FontAwesomeIcon icon={faExternalLinkAlt} />
                      </button>
                    )}
                    <span className="item-value">
                      {summary.mostValuableItem.isLocked ? '$ ***' : `$${summary.mostValuableItem.value.toLocaleString()}`}
                    </span>
                  </div>
                )}
              </div>
            )}

            {/* Quick Stats */}
            <div className="overview-quick">
              {overdueReminders.length > 0 && (
                <div className="quick-alert warning" onClick={() => setActiveTab('reminders')}>
                  <FontAwesomeIcon icon={faExclamationTriangle} />
                  <span>{overdueReminders.length} {t('smartHub.overdueReminders')}</span>
                  <FontAwesomeIcon icon={faArrowRight} />
                </div>
              )}
              {activeInsights.length > 0 && (
                <div className="quick-alert info" onClick={() => setActiveTab('insights')}>
                  <FontAwesomeIcon icon={faLightbulb} />
                  <span>{activeInsights.length} {t('smartHub.insightsAvailable')}</span>
                  <FontAwesomeIcon icon={faArrowRight} />
                </div>
              )}
              {notes.filter(n => n.isPinned).length > 0 && (
                <div className="quick-alert neutral" onClick={() => setActiveTab('notes')}>
                  <FontAwesomeIcon icon={faThumbtack} />
                  <span>{notes.filter(n => n.isPinned).length} {t('smartHub.pinnedNotes')}</span>
                  <FontAwesomeIcon icon={faArrowRight} />
                </div>
              )}
            </div>
          </div>
        )}

        {/* ==================== REMINDERS TAB ==================== */}
        {activeTab === 'reminders' && (
          <div className="reminders-tab">
            <div className="tab-toolbar">
              <div className="filter-group">
                <FontAwesomeIcon icon={faFilter} />
                <select value={reminderFilter} onChange={(e) => setReminderFilter(e.target.value as any)}>
                  <option value="all">{t('smartHub.all')}</option>
                  <option value="active">{t('smartHub.active')}</option>
                  <option value="completed">{t('smartHub.completed')}</option>
                </select>
              </div>
              <button className="add-btn" onClick={() => setShowReminderForm(true)}>
                <FontAwesomeIcon icon={faPlus} />
                {t('smartHub.addReminder')}
              </button>
            </div>

            {showReminderForm && (
              <div className="new-form">
                <div className="form-header">
                  <h4>{editingReminder ? t('smartHub.editReminder') : t('smartHub.newReminder')}</h4>
                  <button onClick={() => { setShowReminderForm(false); setEditingReminder(null); }}><FontAwesomeIcon icon={faTimes} /></button>
                </div>
                <div className="form-body">
                  <input
                    type="text"
                    placeholder={t('smartHub.reminderTitle')}
                    value={newReminder.title}
                    onChange={(e) => setNewReminder(prev => ({ ...prev, title: e.target.value }))}
                    autoFocus
                  />
                  <textarea
                    placeholder={t('smartHub.descriptionOptional')}
                    value={newReminder.description}
                    onChange={(e) => setNewReminder(prev => ({ ...prev, description: e.target.value }))}
                  />
                  <div className="form-row">
                    <select value={newReminder.type} onChange={(e) => setNewReminder(prev => ({ ...prev, type: e.target.value }))}>
                      <option value="custom">{t('smartHub.custom')}</option>
                      <option value="maintenance">{t('smartHub.maintenance')}</option>
                      <option value="review">{t('smartHub.review')}</option>
                      <option value="insurance">{t('smartHub.insurance')}</option>
                      <option value="warranty">{t('smartHub.warranty')}</option>
                    </select>
                    <select value={newReminder.priority} onChange={(e) => setNewReminder(prev => ({ ...prev, priority: e.target.value }))}>
                      <option value="low">{t('smartHub.low')}</option>
                      <option value="medium">{t('smartHub.medium')}</option>
                      <option value="high">{t('smartHub.high')}</option>
                      <option value="urgent">{t('smartHub.urgent')}</option>
                    </select>
                  </div>
                  <div className="form-row">
                    <select
                      value={newReminder.assigneeId}
                      onChange={(e) => setNewReminder(prev => ({ ...prev, assigneeId: e.target.value }))}
                    >
                      <option value="">{t('smartHub.unassigned')}</option>
                      {currentUser && !workspaceMembers.find(m => m.userId === currentUser.id) && (
                        <option value={currentUser.id}>{t('smartHub.me')}</option>
                      )}
                      {workspaceMembers.map(member => (
                        <option key={member.userId} value={member.userId}>
                          {member.userId === currentUser?.id ? t('smartHub.me') : formatUserLabel(member.user, t)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <p className="form-hint">
                    Assign to: “Me” means it will show on your list. Colleagues are your workspace members.
                  </p>
                  <div className="form-row date-time-row">
                    <input
                      type="date"
                      value={newReminder.dueDate}
                      onChange={(e) => setNewReminder(prev => ({ ...prev, dueDate: e.target.value }))}
                    />
                    <input
                      type="time"
                      value={newReminder.dueTime}
                      onChange={(e) => setNewReminder(prev => ({ ...prev, dueTime: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="form-actions">
                  <button className="btn-cancel" onClick={() => { setShowReminderForm(false); setEditingReminder(null); }}>{t('smartHub.cancel')}</button>
                  <button className="btn-save" onClick={handleCreateReminder} disabled={!newReminder.title.trim()}>
                    {editingReminder ? t('smartHub.saveChanges') : t('smartHub.createReminder')}
                  </button>
                </div>
              </div>
            )}

            <div className="reminders-list">
              {filteredReminders.length === 0 ? (
                <div className="empty-state">
                  <FontAwesomeIcon icon={faCalendar} />
                  <p>{t('smartHub.noReminders')}</p>
                  <span>{t('smartHub.noRemindersDesc')}</span>
                </div>
              ) : (
                filteredReminders.map(reminder => (
                  <div
                    key={reminder.id}
                    className={`reminder-item ${reminder.isCompleted ? 'completed' : ''} ${reminder.isAISuggestion ? 'ai-suggestion' : ''} ${reminder.dueDate && new Date(reminder.dueDate) < new Date() && !reminder.isCompleted ? 'overdue' : ''}`}
                  >
                    <button className="checkbox" onClick={() => handleToggleReminder(reminder.id)}>
                      <FontAwesomeIcon icon={reminder.isCompleted ? faCheckCircle : faCheck} />
                    </button>
                    <div className="reminder-content">
                      <div className="reminder-header">
                        <span className="reminder-title">{reminder.title}</span>
                        {reminder.isAISuggestion && (
                          <span className="ai-badge" title={`AI Confidence: ${Math.round((reminder.aiConfidence || 0) * 100)}%`}>
                            <FontAwesomeIcon icon={faRobot} /> AI
                          </span>
                        )}
                      </div>
                      {reminder.description && (
                        <p className="reminder-description">{reminder.description}</p>
                      )}
                      <div className="reminder-meta">
                        <span className="reminder-type" style={{ '--type-color': getPriorityColor(reminder.priority) } as React.CSSProperties}>
                          <FontAwesomeIcon icon={getTypeIcon(reminder.type)} />
                          {reminder.type}
                        </span>
                        {reminder.dueDate && (
                          <span className={`reminder-due ${new Date(reminder.dueDate) < new Date() && !reminder.isCompleted ? 'overdue' : ''}`}>
                            <FontAwesomeIcon icon={faCalendar} />
                            {formatRelativeTime(reminder.dueDate, t)}
                          </span>
                        )}
                        <span className="reminder-priority" style={{ color: getPriorityColor(reminder.priority) }}>
                          {reminder.priority}
                        </span>
                        {reminder.assignee && (
                          <span className="reminder-assignee">
                            {t('smartHub.assignedTo')} {reminder.assignee.id === currentUser?.id ? t('smartHub.me') : formatUserLabel(reminder.assignee, t)}
                          </span>
                        )}
                        {reminder.itemName && (
                          <button className="item-link-inline" onClick={() => navigateToItem(reminder.itemId!)}>
                            <FontAwesomeIcon icon={faExternalLinkAlt} /> {reminder.itemName}
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="reminder-actions">
                      <button 
                        onClick={() => handleToggleReminder(reminder.id)} 
                        title={reminder.isCompleted ? t('smartHub.markIncomplete') : t('smartHub.markComplete')}
                        className={reminder.isCompleted ? 'complete-btn' : 'incomplete-btn'}
                      >
                        <FontAwesomeIcon icon={reminder.isCompleted ? faTimes : faCheck} />
                      </button>
                      <button onClick={() => handleEditReminder(reminder)} title={t('smartHub.editTitle')}>
                        <FontAwesomeIcon icon={faFileAlt} />
                      </button>
                      <button onClick={() => handleDeleteReminder(reminder.id)} title={t('smartHub.deleteTitle')} className="delete-btn">
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* ==================== NOTES TAB ==================== */}
        {activeTab === 'notes' && (
          <div className="notes-tab">
            <div className="tab-toolbar">
              <div className="filter-group">
                <FontAwesomeIcon icon={faFilter} />
                <select value={noteFilter} onChange={(e) => setNoteFilter(e.target.value as any)}>
                  <option value="all">{t('smartHub.allNotes')}</option>
                  <option value="pinned">{t('smartHub.pinned')}</option>
                  <option value="ai-generated">{t('smartHub.aiGenerated')}</option>
                </select>
              </div>
              <button className="add-btn" onClick={() => setShowNoteForm(true)}>
                <FontAwesomeIcon icon={faPlus} />
                {t('smartHub.addNote')}
              </button>
            </div>

            {showNoteForm && (
              <div className="new-form">
                <div className="form-header">
                  <h4>{editingNote ? t('smartHub.editNote') : t('smartHub.newNote')}</h4>
                  <button onClick={() => { setShowNoteForm(false); setEditingNote(null); }}><FontAwesomeIcon icon={faTimes} /></button>
                </div>
                <div className="form-body">
                  <textarea
                    placeholder={t('smartHub.writeNote')}
                    value={newNote.content}
                    onChange={(e) => setNewNote(prev => ({ ...prev, content: e.target.value }))}
                    autoFocus
                    rows={4}
                  />
                  <div className="form-row">
                    <select
                      value={newNote.assigneeId}
                      onChange={(e) => setNewNote(prev => ({ ...prev, assigneeId: e.target.value }))}
                    >
                      <option value="">{t('smartHub.unassigned')}</option>
                      {currentUser && !workspaceMembers.find(m => m.userId === currentUser.id) && (
                        <option value={currentUser.id}>{t('smartHub.me')}</option>
                      )}
                      {workspaceMembers.map(member => (
                        <option key={member.userId} value={member.userId}>
                          {member.userId === currentUser?.id ? t('smartHub.me') : formatUserLabel(member.user, t)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <p className="form-hint">
                    Assign to: “Me” means it will show on your list. Colleagues are your workspace members.
                  </p>
                  <div className="tags-input">
                    <div className="tags-list">
                      {newNote.tags.map(tag => (
                        <span key={tag} className="tag">
                          {tag}
                          <button onClick={() => handleRemoveTag(tag)}><FontAwesomeIcon icon={faTimes} /></button>
                        </span>
                      ))}
                    </div>
                    <div className="tag-input-row">
                      <FontAwesomeIcon icon={faTag} />
                      <input
                        type="text"
                        placeholder={t('smartHub.addTag')}
                        value={newNote.tagInput}
                        onChange={(e) => setNewNote(prev => ({ ...prev, tagInput: e.target.value }))}
                        onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleAddTag())}
                      />
                      <button onClick={handleAddTag}>{t('smartHub.add')}</button>
                    </div>
                  </div>
                </div>
                <div className="form-actions">
                  <button className="btn-cancel" onClick={() => { setShowNoteForm(false); setEditingNote(null); }}>{t('smartHub.cancel')}</button>
                  <button className="btn-save" onClick={handleCreateNote} disabled={!newNote.content.trim()}>
                    {editingNote ? t('smartHub.saveChanges') : t('smartHub.saveNote')}
                  </button>
                </div>
              </div>
            )}

            <div className="notes-list">
              {filteredNotes.length === 0 ? (
                <div className="empty-state">
                  <FontAwesomeIcon icon={faStickyNote} />
                  <p>{t('smartHub.noNotes')}</p>
                  <span>{t('smartHub.noNotesDesc')}</span>
                </div>
              ) : (
                filteredNotes.map(note => (
                  <div key={note.id} className={`note-item ${note.isPinned ? 'pinned' : ''} ${note.isAIGenerated ? 'ai-generated' : ''}`}>
                    <div className="note-content">
                      <p>{note.content}</p>
                      {note.aiContext && (
                        <span className="ai-context"><FontAwesomeIcon icon={faRobot} /> {note.aiContext}</span>
                      )}
                      {note.tags && note.tags.length > 0 && (
                        <div className="note-tags">
                          {note.tags.map(tag => (<span key={tag} className="tag">{tag}</span>))}
                        </div>
                      )}
                      <div className="note-footer">
                        <span className="note-date">{new Date(note.createdAt).toLocaleDateString()}</span>
                        {note.assignee && (
                          <span className="note-assignee">
                            {t('smartHub.assignedTo')} {note.assignee.id === currentUser?.id ? t('smartHub.me') : formatUserLabel(note.assignee, t)}
                          </span>
                        )}
                        {note.itemName && (
                          <button className="item-link-inline" onClick={() => navigateToItem(note.itemId!)}>
                            <FontAwesomeIcon icon={faExternalLinkAlt} /> {note.itemName}
                          </button>
                        )}
                      </div>
                    </div>
                    <div className="note-actions">
                      {note.isAIGenerated && (
                        <span className="ai-badge" title={t('smartHub.aiGenerated')}><FontAwesomeIcon icon={faRobot} /></span>
                      )}
                      <button
                        className={note.isPinned ? 'active' : ''}
                        onClick={() => handleTogglePin(note.id)}
                        title={note.isPinned ? t('smartHub.unpin') : t('smartHub.pinInsight')}
                      >
                        <FontAwesomeIcon icon={faThumbtack} />
                      </button>
                      <button onClick={() => handleEditNote(note)} title={t('smartHub.editTitle')}>
                        <FontAwesomeIcon icon={faFileAlt} />
                      </button>
                      <button onClick={() => handleDeleteNote(note.id)} title={t('smartHub.deleteTitle')} className="delete-btn">
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* ==================== INSIGHTS TAB ==================== */}
        {activeTab === 'insights' && (
          <div className="insights-tab">
            {activeInsights.length === 0 ? (
              <div className="empty-state">
                <FontAwesomeIcon icon={faLightbulb} />
                <p>{t('smartHub.noInsights')}</p>
                <span>{t('smartHub.noInsightsDesc')}</span>
              </div>
            ) : (
              <div className="insights-list">
                {activeInsights.map(insight => (
                  <div key={insight.id} className={`insight-item ${insight.type}`}>
                    <div className="insight-icon">
                      <FontAwesomeIcon icon={
                        insight.type === 'suggestion' ? faLightbulb :
                        insight.type === 'warning' ? faExclamationTriangle :
                        insight.type === 'tip' ? faCheckCircle :
                        insight.type === 'analysis' ? faRobot : faLightbulb
                      } />
                    </div>
                    <div className="insight-content">
                      <h4>{insight.title}</h4>
                      <p>{insight.description}</p>
                      {/* Related Items — interactive links */}
                      {insight.relatedItems && insight.relatedItems.length > 0 && (
                        <div className="insight-items">
                          <span className="items-label">{t('smartHub.relatedItems')}</span>
                          {insight.relatedItems.map(item => (
                            <button
                              key={item.id}
                              className={`item-link ${item.isLocked ? 'locked-item-link' : ''}`}
                              onClick={() => navigateToItem(item.id, item.name, item.isLocked)}
                            >
                              {item.isLocked && <FontAwesomeIcon icon={faLock} style={{ marginRight: 4, fontSize: '0.8em' }} />}
                              {item.isLocked ? maskItemName(item.name) : item.name}
                              {!item.isLocked && <FontAwesomeIcon icon={faExternalLinkAlt} />}
                            </button>
                          ))}
                        </div>
                      )}
                      <div className="insight-meta">
                        <span className="confidence">
                          {t('smartHub.confidence')} {Math.round(insight.confidence * 100)}%
                        </span>
                        {insight.actionLabel && insight.relatedItems && insight.relatedItems.length > 0 && (
                          <button
                            className="action-btn"
                            onClick={() => {
                              const first = insight.relatedItems![0];
                              navigateToItem(first.id, first.name, first.isLocked);
                            }}
                          >
                            {insight.actionLabel} <FontAwesomeIcon icon={faArrowRight} />
                          </button>
                        )}
                      </div>
                    </div>
                    <button className="dismiss-btn" onClick={() => handleDismissInsight(insight.id)} title={t('smartHub.dismiss')}>
                      <FontAwesomeIcon icon={faTimes} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* ==================== AI REPORT TAB ==================== */}
        {activeTab === 'report' && (
          <div className="report-tab">
            {loadingReport ? (
              <div className="ai-loading">
                <FontAwesomeIcon icon={faSpinner} spin />
                <span>{t('smartHub.generatingReport')}</span>
              </div>
            ) : report ? (
              <div className="report-content">
                <div className="report-header-info">
                  <FontAwesomeIcon icon={faFileAlt} />
                  <div>
                    <h4>{t('smartHub.reportTitle')}</h4>
                    <span>{t('smartHub.generated')} {new Date(report.generatedAt).toLocaleString()}</span>
                  </div>
                  <button className="refresh-btn" onClick={fetchReport}>
                    <FontAwesomeIcon icon={faMagic} /> {t('smartHub.regenerate')}
                  </button>
                </div>

                {report.sections.map((section, idx) => (
                  <div key={idx} className={`report-section ${section.type}`}>
                    <h4>{section.title}</h4>
                    <p>{section.content}</p>

                    {/* Overview stats */}
                    {section.type === 'overview' && section.stats && (
                      <div className="report-stats">
                        <div className="stat"><span className="stat-value">{section.stats.totalItems}</span><span className="stat-label">{t('smartHub.items')}</span></div>
                        <div className="stat"><span className="stat-value">${section.stats.totalValue.toLocaleString()}</span><span className="stat-label">{t('smartHub.value')}</span></div>
                        <div className="stat"><span className="stat-value">${section.stats.avgValue.toLocaleString()}</span><span className="stat-label">{t('smartHub.avgValue')}</span></div>
                      </div>
                    )}

                    {/* Category data */}
                    {section.type === 'categories' && Array.isArray(section.data) && (
                      <div className="report-bars">
                        {section.data.map((cat: any) => (
                          <div key={cat.name} className="bar-item">
                            <div className="bar-label">
                              <span>{cat.name}</span>
                              <span>{cat.count} items • ${cat.value.toLocaleString()}</span>
                            </div>
                            <div className="bar-track">
                              <div className="bar-fill" style={{ width: `${Math.min(100, (cat.count / (report?.itemCount || 1)) * 100)}%` }} />
                            </div>
                            {cat.items && (
                              <div className="bar-items">
                                {cat.items.map((item: any) => (
                                  <button key={item.id} className={`item-link-sm ${item.isLocked ? 'locked-item-link' : ''}`} onClick={() => navigateToItem(item.id, item.name, item.isLocked)}>
                                    {item.isLocked ? `🔒 ${maskItemName(item.name)}` : item.name}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Condition data */}
                    {section.type === 'condition' && Array.isArray(section.data) && (
                      <div className="condition-grid">
                        {section.data.map((cond: any) => (
                          <div key={cond.condition} className={`condition-card ${cond.condition.toLowerCase()}`}>
                            <span className="cond-name">{cond.condition.replace('_', ' ')}</span>
                            <span className="cond-count">{cond.count} ({cond.percentage}%)</span>
                            {cond.items && cond.items.length > 0 && (
                              <div className="cond-items">
                                {cond.items.map((item: any) => (
                                  <button key={item.id} className={`item-link-sm ${item.isLocked ? 'locked-item-link' : ''}`} onClick={() => navigateToItem(item.id, item.name, item.isLocked)}>
                                    {item.isLocked ? `🔒 ${maskItemName(item.name)}` : item.name}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Completeness data */}
                    {section.type === 'completeness' && Array.isArray(section.data) && (
                      <div className="completeness-list">
                        {section.data.map((item: any) => (
                          <div key={item.id} className="incomplete-item">
                            <button className={`item-link ${item.isLocked ? 'locked-item-link' : ''}`} onClick={() => navigateToItem(item.id, item.name, item.isLocked)}>
                              {item.isLocked && <FontAwesomeIcon icon={faLock} style={{ marginRight: 4, fontSize: '0.8em' }} />}
                              {item.isLocked ? maskItemName(item.name) : item.name}
                              {!item.isLocked && <FontAwesomeIcon icon={faExternalLinkAlt} />}
                            </button>
                            <div className="missing-tags">
                              {item.missing.map((m: string) => (
                                <span key={m} className="missing-tag">{t('smartHub.missing')} {m}</span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Recommendations */}
                    {section.type === 'recommendations' && Array.isArray(section.data) && (
                      <ul className="recommendations-list">
                        {section.data.map((rec: string, i: number) => (
                          <li key={i}>{rec}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">
                <FontAwesomeIcon icon={faFileAlt} />
                <p>{t('smartHub.noReport')}</p>
                <button className="generate-report-btn" onClick={fetchReport}>
                  <FontAwesomeIcon icon={faMagic} /> {t('smartHub.generateReport')}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Password Lock Modal for locked items */}
    </div>
  );
};

export default AIReminders;
